import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoginModal } from '@/components/LoginModal';
import { useToast } from '@/hooks/use-toast';
import { captureAttribution, countPageHit } from '@/lib/marketing';

// Houston Media Conference booth page — the QR code on the table lands here.
//
// Phone-first and deliberately light: convention centre wifi is bad, the reader is
// standing up holding a coffee, and the whole thing has to be under a minute. No
// photography, no hero video, nothing that blocks paint.
//
// Three steps in one route rather than three routes, so a back-tap never drops
// someone out of the funnel and the whole flow is one payload.
type Step = 'branch' | 'claim' | 'done';

const CAMPAIGN = 'houston-2026';
// The Monday session the trial enrols you in. Config, not literals scattered
// through the copy — the date moved once already (it was Friday the 25th).
const SESSION_LABEL = 'Monday, Sept 21 · 12pm PT';
const SESSION_START_UTC = '20260921T190000Z';
const SESSION_END_UTC = '20260921T200000Z';
const SESSION_PRODUCT_ID = '6aabe5cacbdf63233cddb14a';

const CREAM = '#fbf3e5';
const INK = 'oklch(0.18 0.005 70)';
const INK_BODY = 'oklch(0.32 0.006 70)';
const INK_MUTED = 'oklch(0.48 0.006 70)';
const HAIRLINE = 'oklch(0.18 0.005 70 / 0.14)';
const YELLOW = '#ffd600';

const mono: React.CSSProperties = {
  fontFamily: 'var(--hj-font-mono, ui-monospace, monospace)',
  fontSize: 11,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  fontWeight: 500,
};

// The marker swipe behind a word — a rough highlighter stroke, not a rectangle.
const Swipe: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{
    display: 'inline-block',
    padding: '0.02em 0.16em',
    background: YELLOW,
    clipPath: 'polygon(2% 8%, 98% 0%, 100% 92%, 0% 100%)',
    transform: 'rotate(-1.5deg)',
  }}>{children}</span>
);

// The trial's last free day, counted from today rather than hardcoded — the
// design's "Oct 17" was explicitly a placeholder.
function trialEndsLabel(): string {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

const HoustonPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  // Coming back from Stripe lands straight on the confirmation.
  const [step, setStep] = useState<Step>(params.get('welcome') === '1' ? 'done' : 'branch');
  const [showLogin, setShowLogin] = useState(false);
  const [starting, setStarting] = useState(false);
  const trialEnds = trialEndsLabel();

  const isAuthenticated = () => !!(
    localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('castingUser')
  );

  // Stamp the campaign even when the QR link arrives without utm params — a
  // scan has to count as a scan. captureAttribution() reads the query string and
  // is first-touch, so this only writes when nothing is stored yet, and App.tsx
  // has already run it once on load.
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).get('utm_campaign')) {
      const u = new URL(window.location.href);
      u.searchParams.set('utm_campaign', CAMPAIGN);
      u.searchParams.set('utm_source', 'booth');
      u.searchParams.set('utm_medium', 'qr');
      window.history.replaceState({}, '', u.toString());
    }
    captureAttribution();
    // The denominator. Without it a booth reports "one signup" and nobody can
    // say whether that's one scan in one or one in eighty.
    countPageHit('hmc');
  }, []);

  const startTrial = async () => {
    if (!isAuthenticated()) { setShowLogin(true); return; }
    setStarting(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const res = await fetch('/api/subscription/redeem-promo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ promoCode: CAMPAIGN }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not start the trial');
      if (data.checkoutUrl) { window.location.href = data.checkoutUrl; return; }
      setStep('done');
    } catch (e: any) {
      toast({ title: 'Something went wrong', description: e.message, variant: 'destructive' });
    } finally {
      setStarting(false);
    }
  };

  const screen: React.CSSProperties = {
    background: CREAM,
    borderRadius: 30,
    padding: '30px 24px',
    minHeight: 660,
    maxWidth: 380,
    margin: '0 auto',
    display: 'grid',
  };

  // Hey Joe beside the conference badge, the lockup the handoff specifies for the
  // producer header. On a booth page it does the work the copy can't: it says we
  // are here, at this, before anyone reads a word. The badge is a JPEG with white
  // corners and the mark itself is a circle, so it gets clipped to one.
  const lockup = (onDark = false) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <img
        src={onDark ? '/lovable-uploads/heyjoe-logo-white.png' : '/brand/wordmark-heyjoe-sm.png'}
        alt="Hey Joe"
        style={{ height: 30, width: 'auto' }}
      />
      <span style={{ width: 1, height: 26, background: onDark ? 'rgba(251,243,229,0.28)' : HAIRLINE }} />
      <img
        src="/brand/houston-media-conference.jpeg"
        alt="Houston Media Conference"
        style={{ height: 42, width: 42, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
      />
    </div>
  );

  return (
    <div style={{ background: '#efe5d8', minHeight: '100vh', padding: '18px 14px 40px' }}>
      {step === 'branch' && (
        <div style={{ ...screen, alignContent: 'center', gap: 22 }}>
          {lockup()}
          <h1 style={{ fontWeight: 800, fontSize: 44, lineHeight: 1, letterSpacing: '-0.035em', color: INK, margin: 0 }}>
            Who are you?
          </h1>
          <div style={{ display: 'grid', gap: 12 }}>
            <button
              type="button"
              onClick={() => setStep('claim')}
              style={{
                minHeight: 104, borderRadius: 22, border: 0, background: YELLOW, color: INK,
                fontSize: 25, fontWeight: 800, letterSpacing: '-0.02em', cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              I&rsquo;m a performer
            </button>
            <button
              type="button"
              onClick={() => navigate(window.location.pathname.startsWith('/hmc') ? '/hmc/producers' : '/houston/producers')}
              style={{
                minHeight: 104, borderRadius: 22, background: '#fff', color: INK,
                border: `1px solid oklch(0.18 0.005 70 / 0.16)`,
                fontSize: 25, fontWeight: 800, letterSpacing: '-0.02em', cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              I cast projects
            </button>
          </div>
        </div>
      )}

      {step === 'claim' && (
        <div style={{ ...screen, alignContent: 'start', gap: 18 }}>
          {lockup()}
          <p style={{ ...mono, color: INK_MUTED, margin: 0 }}>Houston Media Conference</p>
          <h1 style={{ fontWeight: 800, fontSize: 37, lineHeight: 1.05, letterSpacing: '-0.032em', color: INK, margin: 0 }}>
            Claim your <Swipe>Houston</Swipe> offer.
          </h1>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 14 }}>
            {[
              [<><strong>Free account.</strong> Casting on Hey Joe is free — free to post, free to submit.</>],
              [<><strong>A seat at Get Seen on Hey Joe</strong>, Houston edition — {SESSION_LABEL}</>],
              [<><strong>30 days of Hey Joe Plus</strong>, free.</>],
              [<><strong>Two paid classes at member pricing</strong> — Corozine, Wroblewski, Pizzoleo, Blank Page. Up to $40 off.</>],
            ].map((b, i) => (
              <li key={i} style={{ display: 'grid', gridTemplateColumns: '11px 1fr', gap: 12, alignItems: 'start' }}>
                <span style={{ width: 11, height: 11, borderRadius: 999, background: YELLOW, marginTop: 6 }} />
                <span style={{ fontSize: 16.5, lineHeight: 1.5, color: INK_BODY }}>{b}</span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={startTrial}
            disabled={starting}
            style={{
              minHeight: 60, borderRadius: 999, border: 0, background: YELLOW, color: INK,
              fontSize: 18, fontWeight: 800, cursor: starting ? 'wait' : 'pointer', fontFamily: 'inherit',
              opacity: starting ? 0.7 : 1,
            }}
          >
            {starting ? 'One moment…' : 'Start my 30 days →'}
          </button>
          <p style={{ fontSize: 14, lineHeight: 1.5, color: INK_BODY, margin: 0 }}>
            <strong>About $50 of value, free.</strong>
          </p>
          <p style={{ fontSize: 12.5, lineHeight: 1.5, color: INK_MUTED, margin: 0 }}>
            Free for 30 days, then $9.99/mo. Cancel any time before {trialEnds} and you&rsquo;re not charged.
          </p>
        </div>
      )}

      {step === 'done' && (
        <div style={{ ...screen, background: INK, alignContent: 'start', gap: 18 }}>
          {lockup(true)}
          <p style={{ ...mono, color: YELLOW, margin: 0 }}>Houston Media Conference</p>
          <h1 style={{ fontWeight: 800, fontSize: 39, lineHeight: 1.05, letterSpacing: '-0.032em', color: CREAM, margin: 0 }}>
            You&rsquo;re in.
          </h1>
          <div style={{ background: 'oklch(0.26 0.008 70)', borderRadius: 18, padding: '18px 20px', display: 'grid', gap: 8 }}>
            <p style={{ ...mono, color: YELLOW, margin: 0 }}>You&rsquo;re enrolled</p>
            <p style={{ margin: 0, color: CREAM, fontSize: 19, fontWeight: 800, letterSpacing: '-0.02em' }}>Get Seen on Hey Joe</p>
            <p style={{ margin: 0, color: CREAM, opacity: 0.8, fontSize: 14.5 }}>{SESSION_LABEL}</p>
            <a
              href={`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent('Get Seen on Hey Joe — Houston edition')}&dates=${SESSION_START_UTC}/${SESSION_END_UTC}`}
              target="_blank" rel="noreferrer"
              style={{
                justifySelf: 'start', marginTop: 4, background: YELLOW, color: INK, borderRadius: 999,
                padding: '10px 18px', fontWeight: 800, fontSize: 14, textDecoration: 'none', minHeight: 40,
                display: 'inline-flex', alignItems: 'center',
              }}
            >
              Add to calendar
            </a>
            <a href={`/api/creator/products/${SESSION_PRODUCT_ID}/calendar.ics`}
               style={{ color: CREAM, opacity: 0.75, fontSize: 12.5, textDecoration: 'underline' }}>
              Apple Calendar (.ics)
            </a>
          </div>
          <div style={{ background: 'oklch(0.26 0.008 70)', borderRadius: 18, padding: '18px 20px', display: 'grid', gap: 6 }}>
            <p style={{ ...mono, color: YELLOW, margin: 0 }}>Hey Joe Plus</p>
            <p style={{ margin: 0, color: CREAM, fontSize: 14.5, lineHeight: 1.5 }}>
              Active now, free through {trialEnds}. Then $9.99/mo. Cancel any time before that and you&rsquo;re not charged.
            </p>
          </div>
          <div style={{ background: 'oklch(0.26 0.008 70)', borderRadius: 18, padding: '18px 20px', display: 'grid', gap: 6 }}>
            <p style={{ ...mono, color: YELLOW, margin: 0 }}>Next</p>
            <p style={{ margin: 0, color: CREAM, fontSize: 14.5, lineHeight: 1.5 }}>
              Add a headshot and a reel when you&rsquo;re home.{' '}
              <a href="/marketplace" style={{ color: YELLOW, fontWeight: 700 }}>Browse breakdowns now.</a>
            </p>
          </div>
          <p style={{ fontSize: 12.5, lineHeight: 1.5, color: CREAM, opacity: 0.6, margin: 0 }}>
            A confirmation and your session link are on the way to the email you gave Stripe.
          </p>
        </div>
      )}

      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onSuccess={() => { setShowLogin(false); startTrial(); }}
      />
    </div>
  );
};

export default HoustonPage;
